<template>
  <input
    :id="id"
    ref="inputRef"
    :aria-describedby="ariaDescribedby || undefined"
    :aria-invalid="ariaInvalid || undefined"
    :aria-label="ariaLabel || undefined"
    class="currency-input"
    :disabled="disabled"
    :placeholder="placeholder"
    :readonly="readonly"
    :required="required"
    type="text"
  />
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { CurrencyDisplay, useCurrencyInput } from 'vue-currency-input';

// Define options type based on useCurrencyInput parameters
type CurrencyInputOptions = Parameters<typeof useCurrencyInput>[0];

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_LOCALE = 'en-AU';

// Financial validation constants
const MAX_SAFE_FINANCIAL_VALUE = 999999999999.99; // ~1 trillion with cents
const MIN_FINANCIAL_VALUE = 0;

defineOptions({
  inheritAttrs: false,
});

/**
 * Currency input with automatic formatting and minor units handling
 *
 * Provides locale-aware currency input with automatic formatting. Handles
 * values in minor units (cents) internally while displaying major units
 * (dollars) to users. Includes financial validation and safe number conversion.
 *
 * **Important:** modelValue should be in minor units (cents):
 * - Display: $100.00 → modelValue: 10000 (cents)
 * - Display: $1,234.56 → modelValue: 123456 (cents)
 *
 * @example Basic currency input
 * ```vue
 * <CurrencyInput
 *   v-model="amountInCents"
 *   currency="USD"
 *   placeholder="Enter amount"
 * />
 * <!-- If user types "$50.25", modelValue becomes 5025 -->
 * ```
 *
 * @example With FormControl
 * ```vue
 * <FormControl
 *   v-model="paymentAmount"
 *   type="currency"
 *   label="Payment Amount"
 *   currency="AUD"
 *   :error="errors.amount"
 *   required
 * />
 * ```
 *
 * @example With value range limits
 * ```vue
 * <CurrencyInput
 *   v-model="depositAmount"
 *   currency="USD"
 *   :min="1000"
 *   :max="1000000"
 * />
 * <!-- Limits: $10.00 minimum, $10,000.00 maximum -->
 * ```
 *
 * @example Different currency
 * ```vue
 * <CurrencyInput
 *   v-model="priceInCents"
 *   currency="EUR"
 *   placeholder="Enter price in euros"
 * />
 * ```
 */
export interface CurrencyInputProps {
  /**
   * Current value in minor units (cents)
   * Example: 12345 represents $123.45
   * Must be non-negative and finite
   * @default undefined
   */
  modelValue: number | string | null | undefined;

  /**
   * ISO 4217 currency code
   * @default 'USD'
   */
  currency?: string;

  /**
   * Placeholder text shown when input is empty
   * @default undefined
   */
  placeholder?: string;

  /**
   * Disables input and prevents interaction
   * @default false
   */
  disabled?: boolean;

  /**
   * Makes input read-only (visible but not editable)
   * @default false
   */
  readonly?: boolean;

  /**
   * Marks field as required for form validation
   * @default false
   */
  required?: boolean;

  /**
   * Minimum value in minor units (cents)
   * @default undefined
   */
  min?: number;

  /**
   * Maximum value in minor units (cents)
   * @default undefined (capped at ~1 trillion)
   */
  max?: number;

  /** Unique element ID for label association */
  id?: string;

  /** Accessible label for screen readers */
  ariaLabel?: string;

  /** ID of element describing this input */
  ariaDescribedby?: string;

  /** Indicates the input has an error for accessibility */
  ariaInvalid?: boolean;
}

const props = withDefaults(defineProps<CurrencyInputProps>(), {
  currency: DEFAULT_CURRENCY,
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const formatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
  style: 'currency',
  currency: props.currency,
});
const decimalPlaces = formatter.resolvedOptions().maximumFractionDigits || 2;

const options: CurrencyInputOptions = {
  locale: DEFAULT_LOCALE,
  currency: props.currency,
  currencyDisplay: CurrencyDisplay.code,
  hideNegligibleDecimalDigitsOnFocus: false,
  useGrouping: true,
  accountingSign: false,
  valueRange: {
    min: props.min !== undefined ? props.min / Math.pow(10, decimalPlaces) : 0,
    max: props.max !== undefined ? props.max / Math.pow(10, decimalPlaces) : undefined,
  },
};

const currencyInput = useCurrencyInput(options, false);
const inputRef = currencyInput.inputRef;
void inputRef; // Used via template ref="inputRef"
const { numberValue, setValue } = currencyInput;

const minorToMajorUnits = (minorUnits: number) => {
  return minorUnits / Math.pow(10, decimalPlaces);
};

const majorToMinorUnits = (majorUnits: number) => {
  return Math.round(majorUnits * Math.pow(10, decimalPlaces));
};

// Financial value validation functions
const isValidFinancialNumber = (value: unknown): value is number => {
  if (typeof value !== 'number') return false;
  if (!Number.isFinite(value)) return false;
  if (Number.isNaN(value)) return false;
  return true;
};

const validateFinancialValue = (value: number): boolean => {
  if (!isValidFinancialNumber(value)) return false;
  if (value < MIN_FINANCIAL_VALUE) return false;
  if (value > MAX_SAFE_FINANCIAL_VALUE) return false;
  return true;
};

const safeNumberConversion = (value: unknown, fallback = 0): number => {
  // Handle null/undefined
  if (value === null || value === undefined) return fallback;

  // Handle string conversion
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isValidFinancialNumber(parsed) ? parsed : fallback;
  }

  // Handle number
  if (typeof value === 'number') {
    return isValidFinancialNumber(value) ? value : fallback;
  }

  return fallback;
};

onMounted(() => {
  try {
    const initialMinorUnits = safeNumberConversion(props.modelValue, 0);

    if (validateFinancialValue(initialMinorUnits)) {
      const initialMajorUnits = minorToMajorUnits(initialMinorUnits);
      setValue(initialMajorUnits);
    } else {
      console.warn('CurrencyInput: Invalid initial value provided:', props.modelValue);
      setValue(0);
    }
  } catch (error) {
    console.error('CurrencyInput: Error during initialization:', error);
    setValue(0);
  }
});

watch(
  () => props.modelValue,
  newMinorUnits => {
    try {
      const safeMinorUnits = safeNumberConversion(newMinorUnits, 0);

      if (!validateFinancialValue(safeMinorUnits)) {
        console.warn('CurrencyInput: Invalid value received:', newMinorUnits);
        return;
      }

      const majorUnits = minorToMajorUnits(safeMinorUnits);
      const currentMajorUnits = safeNumberConversion(numberValue.value, 0);

      // Only update if there's a significant difference to avoid feedback loops
      if (Math.abs(majorUnits - currentMajorUnits) > 0.001) {
        setValue(majorUnits);
      }
    } catch (error) {
      console.error('CurrencyInput: Error in modelValue watcher:', error);
    }
  },
);

watch(numberValue, newMajorUnits => {
  try {
    if (newMajorUnits !== null && newMajorUnits !== undefined) {
      if (!isValidFinancialNumber(newMajorUnits)) {
        console.warn('CurrencyInput: Invalid major units from external library:', newMajorUnits);
        return;
      }

      const minorUnits = majorToMinorUnits(newMajorUnits);

      if (!validateFinancialValue(minorUnits)) {
        console.warn('CurrencyInput: Converted minor units out of valid range:', minorUnits);
        return;
      }

      const currentModelValue = safeNumberConversion(props.modelValue, 0);

      if (minorUnits !== currentModelValue) {
        emit('update:modelValue', minorUnits);
      }
    }
  } catch (error) {
    console.error('CurrencyInput: Error in numberValue watcher:', error);
  }
});
</script>

<style>
.currency-input {
  width: var(--input-width);
  padding: var(--input-padding);
  line-height: var(--input-line-height);
  border-width: var(--input-border-width-top) var(--input-border-width-right) var(--input-border-width-bottom)
    var(--input-border-width-left);
  border-style: solid;
  border-color: var(--input-border-color);
  border-radius: var(--input-border-radius-left) var(--input-border-radius-right) var(--input-border-radius-right)
    var(--input-border-radius-left);
  font-family: var(--input-font-family);
  font-size: var(--input-font-size);
  background-color: var(--input-background);
  transition: var(--input-transition);
  &:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring-color);
    outline-offset: var(--focus-ring-offset);
  }

  &:disabled {
    background-color: var(--input-disabled-background);
    cursor: not-allowed;
    opacity: var(--input-disabled-opacity);
  }

  &::placeholder {
    color: var(--color-neutral-500);
  }
}
</style>
